import React, { useState, useEffect } from 'react'
import { X, ChevronDown, Calendar } from 'lucide-react'
import { toast } from 'react-hot-toast'
import prePaymentService from '../../../services/prePaymentService'
import employeeService from '../../../services/employeeService'

const EditPrePaymentModal = ({ isOpen, onClose, onSuccess, prePayment }) => {
  const [formData, setFormData] = useState({
    amount: '',
    note: ''
  })
  const [employees, setEmployees] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Initialize form data when modal opens or prePayment changes
  useEffect(() => {
    if (isOpen && prePayment) {
      fetchEmployees()
      setFormData({
        amount: prePayment.amount || '',
        note: prePayment.note || ''
      })
    }
  }, [isOpen, prePayment])

  const fetchEmployees = async () => {
    try {
      const response = await employeeService.getAllEmployees()
      if (response.success) {
        const employeesData = response.data.employees || []
        setEmployees(employeesData.filter(emp => emp.isActive))
      }
    } catch (error) {
      console.error('Error fetching employees:', error)
      toast.error('Failed to fetch employees')
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const validateForm = () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount')
      return false
    }
    
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    setIsSubmitting(true)
    
    try {
      const submitData = {
        amount: parseFloat(formData.amount),
        note: formData.note || ''
      }
      
      const response = await prePaymentService.updatePrePayment(prePayment._id, submitData)
      
      if (response.success) {
        toast.success(response.message || 'Pre-payment updated successfully', {
          style: {
            background: '#10B981',
            color: '#FFFFFF',
          },
        })
        onSuccess()
        onClose()
      } else {
        toast.error(response.message || 'Failed to update pre-payment')
      }
    } catch (error) {
      console.error('Error updating pre-payment:', error)
      toast.error('Failed to update pre-payment')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatMonth = (monthStr) => {
    if (!monthStr) return 'N/A'
    
    // Handle both date objects and YYYY-MM strings
    const date = new Date(monthStr)
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      // Try parsing as YYYY-MM format
      const dateFromYearMonth = new Date(monthStr + '-01')
      if (isNaN(dateFromYearMonth.getTime())) {
        return 'N/A'
      }
      return dateFromYearMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    }
    
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp._id === employeeId)
    return employee ? employee.fullName : prePayment?.employee?.fullName || 'N/A'
  }

  if (!isOpen || !prePayment) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Edit Pre Payment</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Read-only Employee Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Employee - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employee
                </label>
                <div className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600">
                  {getEmployeeName(prePayment.employee?._id || prePayment.employee)}
                </div>
              </div>

              {/* Month - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Month
                </label>
                <div className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 flex items-center">
                  <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                  {formatMonth(prePayment.month)}
                </div>
              </div>
            </div>

            {/* Editable Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount *
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                required
              />
            </div>

            {/* Editable Note */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note
              </label>
              <textarea
                name="note"
                value={formData.note}
                onChange={handleInputChange}
                placeholder="Type the reason"
                rows={4}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-vertical"
              />
            </div>
          </form>
        </div>

        {/* Footer - Fixed */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-orange-400 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Updating...' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditPrePaymentModal
